import { RichTextAdvancedRender } from '@framework/engine/inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { type IWidgetParsedProps } from '@framework/engine/schema/widgets/repository/inputs/common/util/getWidgetProps.ts';
import { useInputController } from '@framework/form/hooks/useInputController.ts';
import { DraggerInput } from '@framework/form/inputs/upload/DraggerInput.tsx';
import { UploadInput } from '@framework/form/inputs/upload/UploadInput.tsx';
import { Modal, message } from 'antd';
import { type RcFile } from 'antd/es/upload';
import { useCallback, useRef } from 'react';
import { useUploadWidget } from '../hooks/useUploadWidget.tsx';
import { useUploadWidgetHelpText } from '../hooks/useUploadWidgetHelpText.ts';
import { type IUploadWidget } from '../IUploadWidget.ts';
import { UploadWidgetFileList } from './UploadWidgetFileList.tsx';

const B_TO_MB = 1024 * 1024; // 1MB = 1024 KB = 1024*1024 B

interface IUploadWidgetInternalComponentProps {
  isButton: boolean;

  widget: IUploadWidget;
  options: IWidgetParsedProps;
  publicUpload: boolean | null | undefined;
  attachmentUniqueId: string | undefined;
  readonly: boolean;
}

/**
 * Effettivo componente di upload. Utilizziamo un componente separato per poter
 * usare gli hooks per il context che richiedono essere all'interno del FormItem.
 */
export function UploadWidgetInternalComponent(
  props: IUploadWidgetInternalComponentProps,
) {
  const {
    isButton,
    widget,
    options,
    publicUpload,
    attachmentUniqueId,
    readonly,
  } = props;

  const {
    title,
    fieldName,
    sectionId,
    maxFileSize,
    maxFileSizeMessage,
    fileTypes,
    submissionId,
    submissionModuleId,
    upload,
    filenameFieldName,
  } = useUploadWidget({ widget, options, publicUpload });

  const helpText = useUploadWidgetHelpText({
    widget,
    options,
    publicUpload,
  });

  const {
    field: { value: fileValue, onChange: onFileChange },
  } = useInputController(fieldName, {
    enabled: !readonly,
  });

  const {
    field: { value: filenameValue, onChange: onFilenameChange },
  } = useInputController(filenameFieldName, {
    enabled: !readonly,
    // Lo schema [0] è quello del fileId, [1] è quello del filename
    customSchemaIndex: 1,
  });

  const handleCustomRequest = useCallback(
    (options: any) => {
      const { file, onError } = options;

      if (!file) {
        console.error('[Upload] Error: file not found');
        return;
      }

      if (!submissionId || !submissionModuleId) {
        return;
      }

      // Se il widget è configurato come sensibile, bisogna informare l'API di upload.
      const prevedeDatiSensibili = widget.directives?.omissis?.enabled;

      upload
        .mutateAsync({
          id: submissionId,
          codice: widget.code,
          data: { file },
          idRichiestaModulo: submissionModuleId,
          idSezione: sectionId,
          params: {
            // Indica se il widget necessita di documento pubblico/privato
            prevedeDatiSensibili,
            // Indica se il file che si sta caricando è pubblico o privato
            omissis: !!publicUpload,
            idAllegatoPrecedente: attachmentUniqueId,
          },
        })
        .then(uploadResult => {
          onFilenameChange(uploadResult.nomeFile);
          onFileChange(uploadResult.idFile);
          message.success('File caricato con successo');
        })
        .catch(err => {
          console.error('[Upload] Error after API', err);
          onError?.(err);
          message.error(
            err.response?.data?.descrizione ??
              'Errore durante il caricamento del file',
          );
        });
    },
    [
      submissionId,
      submissionModuleId,
      widget.directives?.omissis?.enabled,
      widget.code,
      upload,
      sectionId,
      publicUpload,
      attachmentUniqueId,
      onFilenameChange,
      onFileChange,
    ],
  );

  const handleBeforeUpload = useCallback(
    (file: RcFile) => {
      // FILE NAME CHECK
      // Only letters, number, "-", "_", " " and the last "." are allowed
      const regex = /^[a-zA-Z0-9-_. ]+$/;

      // Consentire eccezione per i file .pdf.p7m
      const testedFileName = file.name.toLowerCase().endsWith('.p7m')
        ? file.name.replace(/\.p7m$/i, '')
        : file.name;

      if (!regex.test(testedFileName) || testedFileName.split('.').length > 2) {
        // biome-ignore format: <tbd>
        message.error(`Il nome file "${file.name}" non è valido. Sono ammessi solo numeri, lettere, spazi, "-" e "_".`,);
        return false;
      }

      // SIZE CHECK
      if (maxFileSize > 0 && file.size / B_TO_MB > maxFileSize) {
        const fileSize = Math.ceil((file.size / B_TO_MB) * 100) / 100;
        message.error(
          maxFileSizeMessage ??
            `Il file ${file.name} è troppo grande (${fileSize} MB). Dimensione massima: ${maxFileSize} MB`,
        );
        return false;
      }

      return true;
    },
    [maxFileSize, maxFileSizeMessage],
  );

  const hasSubmissionIds = Boolean(submissionId && submissionModuleId);

  const confirmed = useRef(false);

  return (
    <>
      {isButton ? (
        <UploadInput
          disabled={!hasSubmissionIds}
          accept={fileTypes.join(',')}
          name={fieldName}
          title={title}
          multiLineButton
          buttonProps={{
            loading: upload.isLoading,
            onClick: e => {
              // Se è già presente un file e non è stato confermato il sovrascrivere
              // chiediamo conferma con una modal
              if (fileValue && !confirmed.current) {
                Modal.confirm({
                  title: 'Attenzione',
                  content:
                    "E' possibile caricare un solo file. Vuoi sovrascrivere il file caricato?",
                  okText: 'Sovrascrivi',
                  cancelText: 'Annulla',
                  onOk: () => {
                    // Confermo la sovrascrittura e rilancio l'evento
                    // in questo modo è come se "ripremessi" il pulsante,
                    // ma questa volta l'if verrà saltato (confirmed = true) e
                    // si avvierà l'upload
                    confirmed.current = true;
                    e.target.dispatchEvent(e.nativeEvent);
                  },
                });
                e.stopPropagation();
              }

              // Se arrivo qui o non ci sono file o è stata confermata la sovrascrittura
              // reimposto il flag e continuo (l'event non viene stoppato e arriva all'uploadInput)
              confirmed.current = false;
            },
          }}
          customRequest={handleCustomRequest}
          beforeUpload={handleBeforeUpload}
          showUploadList={false}
        />
      ) : (
        <DraggerInput
          name={options.name}
          title={title}
          info={helpText ? <RichTextAdvancedRender text={helpText} /> : <div />}
          customRequest={handleCustomRequest}
          beforeUpload={handleBeforeUpload}
          disabled={!hasSubmissionIds}
        />
      )}
      <UploadWidgetFileList
        publicUpload={publicUpload}
        widget={widget}
        sectionId={sectionId}
        submissionId={submissionId}
        submissionModuleId={submissionModuleId}
        fileId={fileValue}
        filename={filenameValue}
        onFileChange={onFileChange}
        onFilenameChange={onFilenameChange}
      />
    </>
  );
}
